/**
 * React hook for GPU compute in Helios.
 *
 * Provides the best available compute engine (WebGPU > WebGL > CPU),
 * auto-initializes on mount, and exposes backend info for UI display.
 */

import { useState, useEffect, useRef } from 'react';
import GPUEngine from '../gpu';
import type { GPUBackend, GPUEngineInterface } from '../gpu';

export interface UseGPUResult {
  /** The active compute engine (null until initialized) */
  engine: GPUEngineInterface | null;
  /** Whether the engine has finished initializing */
  isReady: boolean;
  /** Which backend is active: 'webgpu' | 'webgl' | 'cpu' */
  backend: GPUBackend | null;
  /** Capabilities of the active backend */
  capabilities: unknown;
  /** Error if initialization failed */
  error: Error | null;
  /** Re-initialize the engine */
  reinitialize: () => void;
}

export function useGPU(): UseGPUResult {
  const [isReady, setIsReady] = useState(GPUEngine.isReady);
  const [backend, setBackend] = useState<GPUBackend | null>(GPUEngine.backend);
  const [capabilities, setCapabilities] = useState<unknown>(null);
  const [error, setError] = useState<Error | null>(null);
  const initRef = useRef(false);

  const initialize = () => {
    initRef.current = false;
    setError(null);
    setIsReady(false);
    setBackend(null);
    setCapabilities(null);

    GPUEngine.init()
      .then((activeBackend) => {
        initRef.current = true;
        setIsReady(true);
        setBackend(activeBackend);
        setCapabilities(GPUEngine.getCapabilities());
      })
      .catch((err) => {
        console.error('[useGPU] Initialization failed:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      });
  };

  useEffect(() => {
    if (GPUEngine.isReady) {
      setIsReady(true);
      setBackend(GPUEngine.backend);
      setCapabilities(GPUEngine.getCapabilities());
      return;
    }

    initialize();

    return () => {
      // Do NOT dispose on unmount -- singleton is shared across components
    };
  }, []);

  return {
    engine: isReady ? GPUEngine.getEngine() : null,
    isReady,
    backend,
    capabilities,
    error,
    reinitialize: initialize,
  };
}

export default useGPU;
